import {graphql, useStaticQuery, Link as GatsbyLink} from 'gatsby';
import React, {Fragment, memo, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import CSSTransition from 'react-transition-group/CSSTransition';

import ArrowLeft from '../images/arrow_left.svg';
import ArrowRight from '../images/arrow_right.svg';
import {useDispatch, useSelector} from '../store';

import {styleMods, unreachable} from '../utils';

import {usePageProps} from '../hooks';

import style from './nav.module.scss';

import IntlString from './intl-string';
import LocaleLink from './locale-link';
import Link from './link';

import SocialFacebook from '../images/social_facebook.svg';
import SocialInstagram from '../images/social_instagram.svg';

/** @typedef {{type: "simple", title: string, path: string}
 *   | {type: "artists"}
 *   | {type: "artists-back"}
 *   | {
 *     type: "artist",
 *     target: string,
 *     text: import('../../tmp/graphql-types').IntlStringFragment,
 *   }
 *   | {
 *     type: "externalProfile",
 *     profileType: "facebook" | "instagram",
 *     name: string,
 *   }
 *   | {
 *     type: "external",
 *     title: string,
 *     href: string,
 *   }
 * } NavItem */

/** @typedef {(_: "root" | "artists") => void} SetCurPanel */

/**
 * @param {import("../page-metadata").PageMetadata} pageMeta
 * @param {import("react-intl").IntlShape} intl
 * @param {import("gatsby").PageProps["location"]} location
 * @returns {React.ReactElement} ...
 */
function renderPanelLangButton(pageMeta, intl, location) {
	const pagePath = pageMeta[intl.locale === 'de' ? 'pathEn' : 'pathDe'];
	const to = `${pagePath}${location.search}${location.hash}`;
	return (
		<GatsbyLink
			to={to}
			className={style.itemLangSwitcherLink}
			hrefLang={intl.locale === 'de' ? 'en' : 'de'}
			title={intl.locale === 'de' ? 'English' : 'Deutsch'}
		>
			{intl.locale === 'de' ? 'en' : 'de'}
		</GatsbyLink>
	);
}

/**
 * @param {NavItem} item
 * @param {import('react-intl').IntlShape} intl
 * @param {SetCurPanel} setCurPanel
 * @returns {React.ReactElement} ...
 */
function renderPanelItem(item, intl, setCurPanel) {
	switch (item.type) {
		case 'simple':
			return (
				<LocaleLink
					to={item.path}
					className={style.itemLink}
				>
					<FormattedMessage id={item.title} />
				</LocaleLink>
			);

		case 'artists-back':
			return (
				<button
					className={style.itemLink}
					onClick={() => {
						setCurPanel('root');
					}}
					title={intl.formatMessage({id: 'nav.back'})}
				>
					<ArrowLeft className={style.itemArrowLeft} />
					<FormattedMessage id="nav.back" />
				</button>
			);

		case 'artists':
			return (
				<button
					className={style.itemLink}
					onClick={() => {
						setCurPanel('artists');
					}}
				>
					<span>
						<FormattedMessage id="nav.artists" />
					</span>
					<ArrowRight className={style.itemArrowRight} />
				</button>
			);

		case 'artist':
			return (
				<LocaleLink
					to={`/map#${item.target}`}
					className={style.itemLink}
				>
					<IntlString {...item.text} />
				</LocaleLink>
			);

		case 'externalProfile':
			let [href, content] = (() => {
				switch (item.profileType) {
					case 'facebook':
						return [`https://www.facebook.com/${item.name}/`, (
							<Fragment>
								<SocialFacebook className={style.itemExternalProfileIcon} />
								{"fb.com/"}{item.name}
							</Fragment>
						)];

					case 'instagram':
						return [`https://www.instagram.com/${item.name}/`, (
							<Fragment>
								<SocialInstagram className={style.itemExternalProfileIcon} />
								{"instagram.com/"}{item.name}
							</Fragment>
						)];

					default:
						return unreachable();
				}
			})();

			return (
				<Link
					className={style.itemLink}
					href={href}
				>
					{content}
				</Link>
			);

		case 'external':
			return (
				<Link
					className={style.itemLink}
					href={item.href}
				>
					<FormattedMessage id={item.title} />
				</Link>
			);

		default:
			return unreachable();
	}
}

const Panel = memo(
	/**
	 * @param {{
	 *   items: Array<NavItem>,
	 *   isVisible: boolean,
	 *   setCurPanel: SetCurPanel,
	 *   panelName: string,
	 * }} param0
	 * @returns {React.ReactElement} ...
	 */
	function Panel({items, isVisible, setCurPanel, panelName}) {
		const intl = useIntl();
		// @ts-ignore
		const pageProps = usePageProps();

		return (
			<ul
				className={styleMods(style, 'list', [isVisible && 'visible', panelName])}
			>
				{items.map((item, i) => (
					<li
						key={i}
						className={styleMods(style, 'item', 'type' in item ? item.type : 'default')}
					>
						{i === 0 && pageProps.pageContext.meta && renderPanelLangButton(
							pageProps.pageContext.meta,
							intl,
							pageProps.location,
						)}
						{renderPanelItem(item, intl, setCurPanel)}
					</li>
				))}
			</ul>
		);
	}
);

export default memo(
	function Nav() {
		const open = useSelector((s) => s.navOpen);
		const dispatch = useDispatch();

		/** @type {import("../../tmp/graphql-types").NavQuery} */
		const data = useStaticQuery(graphql`
			query Nav {
				allArtistsJson(filter: {visibleInNav: {eq: true}}, sort: {order: ASC, fields: order}) {
					nodes {
						slug
						name {
							...IntlString
						}
					}
				}
			}
		`);

		const [curPanel, setCurPanel] = useState(
			/** @returns {"artists" | "root"} */
			() => 'root'
		);

		useEffect(
			/**
			 * @returns {undefined | (() => void)} ...
			 */
			() => {
				// reset source
				setCurPanel('root');

				if (!open) {
					return undefined;
				}

				// add escape key listener to close nav
				/**
				 * @param {KeyboardEvent} e
				 */
				function listener(e) {
					if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
						return;
					}

					if (e.key === 'Escape') {
						dispatch({type: 'closeNav'});
						e.preventDefault();
						e.stopPropagation();
					}
				}

				window.addEventListener('keydown', listener);
				return () => {
					window.removeEventListener('keydown', listener);
				};
			},
			[open, setCurPanel, dispatch],
		);

		const panels = [
			{
				panelName: 'root',
				/** @type {Array<NavItem>} */
				items: [
					{type: 'simple', title: 'nav.artworks', path: '/map'},
					{type: 'artists'},
					// {type: 'simple', title: 'nav.blog', path: '/blog'},
					// {type: 'simple', title: 'nav.tours', path: '/tours'},
					// {type: 'simple', title: 'nav.events', path: '/events'},
					// {type: 'simple', title: 'nav.lightsOff', path: '/lights-off'},
					{
						type: 'external',
						title: 'nav.panorama',
						href: 'https://www.braunschweig.de/lp2020pano',
					},
					{type: 'simple', title: 'nav.sponsors', path: '/sponsors'},
					{type: 'simple', title: 'nav.press', path: '/press'},
					{type: 'simple', title: 'nav.contact', path: '/contact'},
					{type: 'externalProfile', profileType: 'facebook', name: 'lichtparcours2020'},
					{type: 'externalProfile', profileType: 'instagram', name: 'lichtparcours'},
				],
			},
			{
				panelName: 'artists',
				items: [
					{type: 'artists-back'},
					...data.allArtistsJson.nodes.map((art) => {
						/** @type {NavItem} */
						const d = {
							type: 'artist',
							text: art.name,
							target: art.slug,
						};
						return d;
					}),
				],
			}
		]
			.map(({panelName, items}) => (
				<Panel
					key={panelName}
					panelName={panelName}
					items={items}
					isVisible={panelName === curPanel}
					setCurPanel={setCurPanel}
				/>
			));

		return (
			<Fragment>
				<CSSTransition
					in={open}
					timeout={400}
					classNames={{
						enter: style.overlayEnter,
						enterActive: style.overlayEnterActive,
						enterDone: style.overlayEnterDone,
						exit: style.overlayExit,
						exitActive: style.overlayExitActive,
					}}
					mountOnEnter={true}
					unmountOnExit={true}
				>
					<div
						className={style.overlay}
						tabIndex={-1}
						onClick={() => {
							dispatch({type: 'closeNav'});
						}}
					/>
				</CSSTransition>

				<CSSTransition
					in={open}
					timeout={400}
					classNames={{
						enter: style.baseEnter,
						enterActive: style.baseEnterActive,
						enterDone: style.baseEnterDone,
						exit: style.baseExit,
						exitActive: style.baseExitActive,
					}}
				>
					<nav className={style.base}>
						{panels}
					</nav>
				</CSSTransition>
			</Fragment>
		);
	}
);
